import { generateUnauthorizedToken, verifyUnauthorizedToken } from 'api/guestSession'
import { useCallback, useEffect, useState } from 'react'

export const AUTH_TOKEN_KEY = 'authorized_token'
export const GUEST_TOKEN_KEY = 'guest_token'

export const useToken = () => {
    const [sessionId, setSessionId] = useState<string | null>(null)
    const [isLoading, setIsLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)

    const setAuthorizedToken = (token: string) => {
        localStorage.setItem(AUTH_TOKEN_KEY, token)
        localStorage.removeItem(GUEST_TOKEN_KEY)
        setSessionId(token)
    }

    const removeAuthorizedToken = () => {
        localStorage.removeItem(AUTH_TOKEN_KEY)
        setSessionId(null)
    }

    const setGuestToken = (token: string) => {
        localStorage.setItem(GUEST_TOKEN_KEY, token)
        localStorage.removeItem(AUTH_TOKEN_KEY)
        setSessionId(token)
    }

    const createGuestToken = useCallback(async () => {
        try {
            // Prevent re-generation if one already exists
            const existing = localStorage.getItem(GUEST_TOKEN_KEY)
            if (existing) return

            const newToken = await generateUnauthorizedToken()
            setGuestToken(newToken)
        } catch (err) {
            console.error('Guest token generation failed:', err)
            setError('Failed to generate guest token.')
        }
    }, [])

    useEffect(() => {
        let isMounted = true

        const initToken = async () => {
            setIsLoading(true)

            try {
                // Wait for Firebase auth to initialize
                if (typeof window !== 'undefined' && !(window as any).firebaseAuthResolved) {
                    await new Promise<void>((resolve) => {
                        const interval = setInterval(() => {
                            if ((window as any).firebaseAuthResolved) {
                                clearInterval(interval)
                                resolve()
                            }
                        }, 50)
                    })
                }

                const authToken = localStorage.getItem(AUTH_TOKEN_KEY)
                if (authToken) {
                    if (isMounted) setSessionId(authToken)
                    return
                }

                const guestToken = localStorage.getItem(GUEST_TOKEN_KEY)
                if (guestToken) {
                    try {
                        await verifyUnauthorizedToken(guestToken)
                        if (isMounted) setSessionId(guestToken)
                        return
                    } catch {
                        console.warn('Invalid guest token, regenerating...')
                        localStorage.removeItem(GUEST_TOKEN_KEY)
                    }
                }

                await createGuestToken()
            } catch (err) {
                console.error('Token handling error:', err)
                if (isMounted) setError('An error occurred during token handling.')
            } finally {
                if (isMounted) setIsLoading(false)
            }
        }

        initToken()

        return () => {
            isMounted = false
        }
    }, [createGuestToken])

    return {
        sessionId,
        isLoading,
        error,
        setAuthorizedToken,
        removeAuthorizedToken,
        createGuestToken,
    }
}
